import React, { useState } from 'react';
import { AlertTriangle, CircleAlert, Info, X } from 'lucide-react';
import { useGetUserDataQuery } from '../services/auth';
import './PlanBanner.css';

// Referenced by the chat input's aria-describedby while asking is blocked.
export const ASK_BLOCKED_MESSAGE_ID = 'plan-banner-ask-message';

const DISMISS_KEY_PREFIX = 'planBanner:dismissed:';

const getDismissKey = (warning) =>
  `${DISMISS_KEY_PREFIX}${warning.code}:${warning.trial_ends_at || ''}`;

const readDismissed = (key) => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch (_) {
    return false;
  }
};

const writeDismissed = (key) => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (_) {
    // Storage unavailable (private mode, quota) — dismissal lasts until remount.
  }
};

const PlanBanner = () => {
  const { data: userData } = useGetUserDataQuery();
  const [dismissedKey, setDismissedKey] = useState(null);

  if (!userData) return null;

  if (userData.can_ask === false) {
    return (
      <div className="plan-banner plan-banner--blocked" role="alert">
        <CircleAlert className="plan-banner__icon" size={16} aria-hidden="true" focusable="false" />
        <p id={ASK_BLOCKED_MESSAGE_ID} className="plan-banner__message">
          {userData.ask_message || "You can't send messages right now."}
        </p>
      </div>
    );
  }

  const warning = userData.plan_warning;
  if (!warning?.message) return null;

  if (warning.code === 'grace_period') {
    return (
      <div className="plan-banner plan-banner--grace" role="status">
        <AlertTriangle className="plan-banner__icon" size={16} aria-hidden="true" focusable="false" />
        <p className="plan-banner__message">{warning.message}</p>
      </div>
    );
  }

  const key = getDismissKey(warning);
  if (dismissedKey === key || readDismissed(key)) return null;

  const handleDismiss = () => {
    writeDismissed(key);
    setDismissedKey(key);
  };

  return (
    <div className="plan-banner plan-banner--ending" role="status">
      <Info className="plan-banner__icon" size={16} aria-hidden="true" focusable="false" />
      <p className="plan-banner__message">{warning.message}</p>
      <button
        type="button"
        className="plan-banner__dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss plan notice"
      >
        <X size={16} aria-hidden="true" focusable="false" />
      </button>
    </div>
  );
};

export default PlanBanner;
